// FiscalDashboardCards — Story 10.7 AC8 + Story 10.8 AC7.
// Card "NFs do mês" + Card "NFs falhando" no dashboard.

import { AlertTriangle, ArrowDown, ArrowUp, FileText, Receipt } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { addDaysLocal, todayLocal } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { useFiscalInvoices } from '../../hooks/use-fiscal';

/**
 * Helper — agrega valor bruto + ISS retido + count de NFs issued
 * (não conta cancelled/failed/discarded).
 */
function aggregate(items: { status: string; amount: string; issAmount: string | null }[]) {
  let total = 0;
  let iss = 0;
  let count = 0;
  for (const it of items) {
    if (it.status !== 'issued') continue;
    const amt = Number.parseFloat(it.amount);
    if (Number.isFinite(amt)) total += amt;
    const i = it.issAmount ? Number.parseFloat(it.issAmount) : 0;
    if (Number.isFinite(i)) iss += i;
    count++;
  }
  return { total, iss, count };
}

function formatBRL(n: number): string {
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

export function FiscalDashboardCards(): JSX.Element {
  // Story 10.7 AC8 — NFs do mês atual + comparativo mês anterior.
  // todayLocal() retorna YYYY-MM-DD. Derivamos primeiro dia do mês atual.
  const { thisMonthStart, lastMonthStart, lastMonthEnd } = useMemo(() => {
    const today = todayLocal(); // YYYY-MM-DD
    const [y, m] = today.split('-').map((s) => Number.parseInt(s, 10));
    const yy = y ?? 1970;
    const mm = m ?? 1;
    const thisStart = `${yy}-${String(mm).padStart(2, '0')}-01`;
    const lastY = mm === 1 ? yy - 1 : yy;
    const lastM = mm === 1 ? 12 : mm - 1;
    const lastStart = `${lastY}-${String(lastM).padStart(2, '0')}-01`;
    const lastEnd = addDaysLocal(thisStart, -1);
    return { thisMonthStart: thisStart, lastMonthStart: lastStart, lastMonthEnd: lastEnd };
  }, []);

  const thisMonth = useFiscalInvoices({
    from_date: thisMonthStart,
    to_date: todayLocal(),
    pageSize: 100,
  });
  const lastMonth = useFiscalInvoices({
    from_date: lastMonthStart,
    to_date: lastMonthEnd,
    pageSize: 100,
  });
  // Story 10.8 AC7 — NFs falhando (failed + pending_data).
  const failing = useFiscalInvoices({ status: ['failed', 'pending_data'], pageSize: 100 });

  const thisAgg = useMemo(
    () => (thisMonth.data ? aggregate(thisMonth.data.data) : null),
    [thisMonth.data],
  );
  const lastAgg = useMemo(
    () => (lastMonth.data ? aggregate(lastMonth.data.data) : null),
    [lastMonth.data],
  );

  const deltaPct =
    thisAgg && lastAgg && lastAgg.total > 0
      ? ((thisAgg.total - lastAgg.total) / lastAgg.total) * 100
      : null;

  const failingCount = failing.data?.pagination.total ?? 0;
  const failingHigh = failingCount > 5;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* NFs do mês — Story 10.7 AC8 */}
      <Link
        to="/fiscal?tab=emitidas"
        className="group flex flex-col gap-3 rounded-[14px] border border-line bg-panel p-5 transition-colors hover:border-line-2"
      >
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-2">
            <Receipt size={12} strokeWidth={2} />
            NFs do mês
          </span>
          {deltaPct !== null && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-[6px] px-2 py-[2px] text-[10px] font-medium',
                deltaPct >= 0 ? 'bg-good/10 text-good' : 'bg-bad/10 text-bad',
              )}
            >
              {deltaPct >= 0 ? (
                <ArrowUp size={10} strokeWidth={2} />
              ) : (
                <ArrowDown size={10} strokeWidth={2} />
              )}
              {Math.abs(deltaPct).toFixed(1)}%
            </span>
          )}
        </div>

        {thisMonth.isLoading ? (
          <div className="h-[60px] animate-pulse rounded-[8px] bg-panel-2" />
        ) : (
          <>
            <div className="tnum text-[22px] font-bold tracking-[-0.025em] text-ink">
              {thisAgg ? formatBRL(thisAgg.total) : 'R$ 0,00'}
            </div>
            <div className="grid grid-cols-2 gap-3 text-[11px]">
              <div>
                <div className="text-muted-3">ISS retido</div>
                <div className="tnum mt-[2px] font-medium text-muted">
                  {thisAgg ? formatBRL(thisAgg.iss) : '—'}
                </div>
              </div>
              <div>
                <div className="text-muted-3">Quantidade</div>
                <div className="tnum mt-[2px] font-medium text-muted">{thisAgg?.count ?? 0}</div>
              </div>
            </div>
            <div className="text-[11px] text-muted-2 group-hover:text-ink">Ver todas as NFs →</div>
          </>
        )}
      </Link>

      {/* NFs falhando — Story 10.8 AC7 */}
      <Link
        to="/webhook-events"
        className={cn(
          'group flex flex-col gap-3 rounded-[14px] border bg-panel p-5 transition-colors',
          failingHigh ? 'border-bad/40 hover:border-bad' : 'border-line hover:border-line-2',
        )}
      >
        <div className="flex items-center justify-between">
          <span
            className={cn(
              'flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider',
              failingHigh ? 'text-bad' : 'text-muted-2',
            )}
          >
            {failingHigh ? (
              <AlertTriangle size={12} strokeWidth={2} />
            ) : (
              <FileText size={12} strokeWidth={2} />
            )}
            NFs falhando
          </span>
          {failingHigh && (
            <span className="rounded-[6px] bg-bad/10 px-2 py-[2px] text-[10px] font-medium text-bad">
              Atenção
            </span>
          )}
        </div>

        {failing.isLoading ? (
          <div className="h-[60px] animate-pulse rounded-[8px] bg-panel-2" />
        ) : (
          <>
            <div
              className={cn(
                'tnum text-[22px] font-bold tracking-[-0.025em]',
                failingHigh ? 'text-bad' : 'text-ink',
              )}
            >
              {failingCount}
            </div>
            <div className="text-[11px] text-muted">
              {failingCount === 0
                ? 'Tudo certo — nenhuma NF em DLQ.'
                : failingCount === 1
                  ? '1 NF precisa de atenção (failed ou dados pendentes).'
                  : `${failingCount} NFs precisam de atenção (failed ou dados pendentes).`}
            </div>
            <div className="text-[11px] text-muted-2 group-hover:text-ink">Abrir DLQ Fiscal →</div>
          </>
        )}
      </Link>
    </div>
  );
}
